// ── SalesAnalyticsPage — pipeline analytics ──
import { useState, useEffect, useMemo } from "react";
import { B } from "../../../shared/theme";
import { fmt } from "../../../shared/format";
import { Card, ErrorMsg, SectionHeader, Spinner } from "../../../shared/ui";
import { useApp } from "../../../shared/appContext";
import { OwnerMultiSelect, isExcludedOwner } from "./analyticsShared";

export const SalesAnalyticsPage = () => {
  const { api } = useApp();
  const [opps, setOpps] = useState([]);
  const [stages, setStages] = useState([]);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [ownerSel, setOwnerSel] = useState(null);   // Sourced By multi-select; null = default (all except excluded four)

  const load = () => {
    setLoading(true);
    Promise.all([api.get("getOpportunities"), api.get("getOpportunityStages"), api.get("getLeads")])
      .then(([o, s, l]) => { setOpps(Array.isArray(o) ? o : []); setStages(Array.isArray(s) ? s : []); setLeads(Array.isArray(l) ? l : []); setLoading(false); })
      .catch(e => { setErr(e.message); setLoading(false); });
  };
  useEffect(() => { load(); }, []);

  // Sourced By (owner) options across opps + leads; default excludes the four but keeps them re-selectable.
  const owners = useMemo(() => [...new Set([...opps, ...leads].map(x => x.ownerName).filter(Boolean))].sort(), [opps, leads]);
  useEffect(() => { if (ownerSel === null && owners.length) setOwnerSel(new Set(owners.filter(o => !isExcludedOwner(o)))); }, [owners, ownerSel]);
  const ownerOk = (name) => !name ? true : (ownerSel ? ownerSel.has(name) : !isExcludedOwner(name));

  if (loading) return <Spinner />;
  if (err) return <ErrorMsg msg={err} onRetry={load} />;

  // Apply the Sourced By filter to both opportunities and leads.
  const fopps = opps.filter(o => ownerOk(o.ownerName));
  const fleads = leads.filter(l => ownerOk(l.ownerName));

  const commit = (x) => parseFloat(x.commitment_amount || 0);
  const weighted = (x) => commit(x) * (parseFloat(x.probability != null ? x.probability : x.stage_probability || 0) / 100);
  const sum = (arr, fn) => arr.reduce((s, x) => s + fn(x), 0);

  const openOpps = fopps.filter(o => !o.is_closed && o.stage !== "Hold");   // Hold excluded from pipeline analytics
  const wonOpps = fopps.filter(o => o.is_won);
  const lostOpps = fopps.filter(o => o.is_closed && !o.is_won);
  const openStages = stages.filter(s => !s.is_closed && s.stage !== "Hold");

  const pipelineTotal = sum(openOpps, commit);
  const weightedTotal = sum(openOpps, weighted);
  const winRate = (wonOpps.length + lostOpps.length) ? (wonOpps.length / (wonOpps.length + lostOpps.length)) : 0;
  const avgOpen = openOpps.length ? pipelineTotal / openOpps.length : 0;

  // Open pipeline by owner
  const byOwnerMap = new Map();
  openOpps.forEach(o => {
    const k = o.ownerName || "—";
    const e = byOwnerMap.get(k) || { name: k, count: 0, amt: 0, wtd: 0 };
    e.count += 1; e.amt += commit(o); e.wtd += weighted(o);
    byOwnerMap.set(k, e);
  });
  const byOwner = [...byOwnerMap.values()].sort((a, b) => b.amt - a.amt);
  const ownerMaxAmt = Math.max(1, ...byOwner.map(o => o.amt));

  // Open leads by source
  const openLeads = fleads.filter(l => !(l.converted_at || l.converted_accountid));
  const bySourceMap = new Map();
  openLeads.forEach(l => { const k = l.lead_source || "Unspecified"; bySourceMap.set(k, (bySourceMap.get(k) || 0) + 1); });
  const bySource = [...bySourceMap.entries()].map(([name, count]) => ({ name, count })).sort((a, b) => b.count - a.count);
  const sourceMax = Math.max(1, ...bySource.map(s => s.count));

  const stageMaxCount = Math.max(1, ...openStages.map(s => openOpps.filter(o => o.stage === s.stage).length));

  const cardTitle = { fontSize: 11, fontWeight: 700, color: B.cyanDark, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 12, paddingBottom: 6, borderBottom: "1.5px solid " + B.cyanLight };
  const stat = { fontSize: 24, fontWeight: 600, lineHeight: 1.1 };
  const statSub = { fontSize: 11, color: "var(--color-text-secondary)", marginTop: 4 };

  const Bar = ({ label, value, max, right, color = B.cyan }) => (
    <div style={{ marginBottom: 10 }}>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 3, gap: 8 }}>
        <span style={{ color: "var(--color-text-secondary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{label}</span>
        <span style={{ fontWeight: 600, whiteSpace: "nowrap" }}>{right}</span>
      </div>
      <div style={{ height: 8, background: "var(--color-background-tertiary)", borderRadius: 4 }}>
        <div style={{ height: 8, width: (max > 0 ? Math.max(2, (value / max) * 100) : 0) + "%", background: color, borderRadius: 4 }} />
      </div>
    </div>
  );

  return <>
    <SectionHeader title="Sales Analytics" />

    {/* slicer */}
    <Card style={{ padding: 12, marginBottom: 16, display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
      <OwnerMultiSelect owners={owners} value={ownerSel} onChange={setOwnerSel} />
      <div style={{ marginLeft: "auto", fontSize: 12, color: "var(--color-text-secondary)" }}>
        {openOpps.length} open · {fmt(pipelineTotal)} pipeline
      </div>
    </Card>

    {/* KPI row */}
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 16, marginBottom: 16 }}>
      <Card style={{ padding: 16 }}><div style={cardTitle}>Open Pipeline</div><div style={stat}>{fmt(pipelineTotal)}</div><div style={statSub}>{openOpps.length} open opportunities</div></Card>
      <Card style={{ padding: 16 }}><div style={cardTitle}>Weighted Forecast</div><div style={stat}>{fmt(weightedTotal)}</div><div style={statSub}>Amount × probability</div></Card>
      <Card style={{ padding: 16 }}><div style={cardTitle}>Win Rate</div><div style={stat}>{Math.round(winRate * 100)}%</div><div style={statSub}>{wonOpps.length} won / {lostOpps.length} lost</div></Card>
      <Card style={{ padding: 16 }}><div style={cardTitle}>Avg Open Deal</div><div style={stat}>{fmt(avgOpen)}</div><div style={statSub}>Commitment per open opp</div></Card>
    </div>

    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
      {/* Conversion by stage */}
      <Card style={{ padding: 16 }}>
        <div style={cardTitle}>Open Pipeline by Stage</div>
        {openStages.length === 0 ? <div style={statSub}>No stages.</div> : openStages.map(s => {
          const list = openOpps.filter(o => o.stage === s.stage);
          const color = s.stage === "Hold" ? "#8a8f98" : s.makes_account_visible ? B.amber : B.cyan;
          return <Bar key={s.stage} label={s.stage} value={list.length} max={stageMaxCount} color={color}
            right={list.length + " · " + fmt(sum(list, commit))} />;
        })}
      </Card>

      {/* Win / Loss */}
      <Card style={{ padding: 16 }}>
        <div style={cardTitle}>Won vs Lost</div>
        <Bar label="Funded (Won)" value={wonOpps.length} max={Math.max(1, wonOpps.length, lostOpps.length)} color={B.amber} right={wonOpps.length + " · " + fmt(sum(wonOpps, commit))} />
        <Bar label="Declined (Lost)" value={lostOpps.length} max={Math.max(1, wonOpps.length, lostOpps.length)} color="#8a8f98" right={lostOpps.length + " · " + fmt(sum(lostOpps, commit))} />
        <div style={{ marginTop: 8, fontSize: 12, color: "var(--color-text-secondary)" }}>
          Win rate <span style={{ fontWeight: 700, color: "var(--color-text-primary)" }}>{Math.round(winRate * 100)}%</span> by count.
        </div>
      </Card>
    </div>

    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
      {/* By owner */}
      <Card style={{ padding: 16 }}>
        <div style={cardTitle}>Open Pipeline by Owner</div>
        {byOwner.length === 0 ? <div style={statSub}>No open opportunities.</div> : byOwner.map(o => (
          <Bar key={o.name} label={o.name} value={o.amt} max={ownerMaxAmt} right={o.count + " · " + fmt(o.amt)} />
        ))}
      </Card>

      {/* Leads by source */}
      <Card style={{ padding: 16 }}>
        <div style={cardTitle}>Open Leads by Source</div>
        {bySource.length === 0 ? <div style={statSub}>No open leads.</div> : bySource.map(s => (
          <Bar key={s.name} label={s.name} value={s.count} max={sourceMax} right={String(s.count)} />
        ))}
      </Card>
    </div>
  </>;
};
